// Public header metadata only. Never derive previews from gated components.
const SITE_ORIGIN = 'https://muultispace.com';
const SITE_DESCRIPTION = 'MuultiSpace is a Web3 web page builder that gives users modular control over how their audiences access their content.';
const SITE_TITLE = 'MuultiSpace | Modular Web3 Page Builder';
const SITE_IMAGE = SITE_ORIGIN + '/muultispace-logo.png';
const RESERVED_ROUTES = new Set([
    'muulti-directory', 'token-gates', 'about', 'collection-creation', 'nft-studio',
    'posts', 'wallet-detail', '3d', 'muultispace-admin', 'legal', 'ads',
]);

const PUBLIC_PAGES = {
    '/': [SITE_TITLE, SITE_DESCRIPTION],
    '/about': ['About MuultiSpace', 'Discover MuultiSpace, a modular Web3 page builder available in the Solana Mobile dApp Store, with multimedia publishing and audience access controls.'],
    '/muulti-directory': ['Creator Directory | MuultiSpace', 'Discover creators and explore their multimedia Spaces on MuultiSpace.'],
    '/posts': ['Social Feed | MuultiSpace', 'Explore public posts, art, music, and updates from MuultiSpace creators.'],
    '/3d/street': ['Explore 3D Spaces | MuultiSpace', 'Explore the MuultiSpace 3D street and visit creator Spaces.'],
    '/nft-studio': ['NFT Studio | MuultiSpace', 'Explore MuultiSpace tools for creating Solana NFTs and publishing digital artwork.'],
    '/collection-creation': ['Create a Collection | MuultiSpace', 'Create a Solana NFT collection using MuultiSpace creator tools.'],
    '/legal/privacy': ['Privacy Policy | MuultiSpace', 'Read the MuultiSpace Privacy Policy, including information about platform data and contact details.'],
    '/legal/license': ['Terms of Use | MuultiSpace', 'Read the terms for using MuultiSpace, publishing content, and interacting with blockchain features.'],
    '/legal/copyright': ['Copyright Policy | MuultiSpace', 'Read the MuultiSpace Copyright Policy and learn how to report content infringement.'],
    '/token-gates': ['Token Gate Tools | MuultiSpace', 'Manage your MuultiSpace token gate lists with your wallet.', false],
    '/wallet-detail': ['Your Wallet | MuultiSpace', 'View your connected wallet and MuultiSpace account tools.', false],
    '/wallet-detail/followers': ['Your Followers | MuultiSpace', 'Manage followers and followed Spaces for your connected wallet.', false],
    '/muultispace-admin': ['Administration | MuultiSpace', 'MuultiSpace administration tools for authorized wallets.', false],
    '/ads/compose': ['Create an Ad | MuultiSpace', 'Create a MuultiSpace advertising campaign with your wallet.', false],
    '/ads/market': ['Ad Marketplace | MuultiSpace', 'Manage MuultiSpace creator advertising placements with your wallet.', false],
};

const COMPONENT_TYPE_LIST_OPTIONS = [
    { type: 'text', label: 'Text', pluralLabel: 'Text', listLabel: 'Text List' },
    { type: 'image', label: 'Image', pluralLabel: 'Images', listLabel: 'Image List' },
    { type: 'audio', label: 'Audio', pluralLabel: 'Audio', listLabel: 'Audio List' },
    { type: 'video', label: 'Video', pluralLabel: 'Videos', listLabel: 'Video List' },
    { type: 'ad', label: 'Ad', pluralLabel: 'Ads', listLabel: 'Ad List' },
    { type: 'posts', label: 'Social Posts', pluralLabel: 'Posts', listLabel: 'Social Posts', showCount: false },
];
const LIST_ALIASES = { texts: 'text', images: 'image', audios: 'audio', videos: 'video', ads: 'ad' };
const COMPONENT_SEGMENTS = { c: 'text', v: 'video', l: 'link', i: 'image', m: 'mint', d: 'donation' };

function normalizeListComponentType(type) {
    const normalized = String(type || '').trim().toLowerCase();
    const nextType = LIST_ALIASES[normalized] || normalized;
    return COMPONENT_TYPE_LIST_OPTIONS.some(option => option.type === nextType) ? nextType : '';
}

function isComponentListAvailable(profile, type) {
    const normalized = normalizeListComponentType(type);
    if (!normalized || profile?.componentListSettings?.[normalized] === false) return false;
    if (normalized === 'posts') return Boolean(profile?.publicKey);
    return (Array.isArray(profile?.components) ? profile.components : [])
        .filter(comp => normalizeListComponentType(comp?.type) === normalized).length >= 2;
}

function plainText(value) {
    return typeof value === 'string'
        ? value.slice(0, 10000).replace(/<[^>]*>/g, ' ').replace(/[\u0000-\u001f\u007f\s]+/g, ' ').trim()
        : '';
}

function excerpt(value, limit) {
    const text = plainText(value);
    const characters = Array.from(text);
    if (characters.length <= limit) return text;
    let shortened = characters.slice(0, limit - 1).join('');
    const lastSpace = shortened.lastIndexOf(' ');
    if (lastSpace > shortened.length * 0.6) shortened = shortened.slice(0, lastSpace);
    return shortened.trimEnd() + '…';
}

function pagePath(pathname) {
    try {
        const path = new URL(pathname || '/', SITE_ORIGIN).pathname;
        return path.replace(/\/+$/, '') || '/';
    } catch {
        return '/';
    }
}

function getSpaceUsername(pathname) {
    const parts = pagePath(pathname).split('/').filter(Boolean);
    if (parts.length !== 1 && !(parts.length === 3 && /^(c|v|l|i|m|d|list)$/.test(parts[1]))) return '';
    let username;
    try { username = decodeURIComponent(parts[0]); } catch { return ''; }
    if (!/^[A-Za-z0-9_-]{1,64}$/.test(username) || RESERVED_ROUTES.has(username.toLowerCase())) return '';
    return username;
}

function getPageRoute(pathname) {
    const path = pagePath(pathname);
    const definition = PUBLIC_PAGES[path.toLowerCase()];
    if (definition) return { kind: 'static', path: path.toLowerCase(), definition };
    if (path.includes('//')) return { kind: 'missing', path };
    let parts;
    try { parts = path.split('/').filter(Boolean).map(decodeURIComponent); } catch { return { kind: 'missing', path }; }
    if (parts.some(part => part.includes('/') || part.length > 128)) return { kind: 'missing', path };
    if (parts.length === 2 && parts[0].toLowerCase() === 'posts' && parts[1]) {
        return { kind: 'post', path, postId: parts[1] };
    }
    if (parts.length === 3 && parts[0].toLowerCase() === '3d' && parts[1].toLowerCase() === 'space') {
        const username = getSpaceUsername('/' + parts[2]);
        if (username) return { kind: 'space3d', path, username };
    }
    const username = getSpaceUsername(path);
    if (!username) return { kind: 'missing', path };
    if (parts.length === 1) return { kind: 'space', path, username };
    if (parts[1] === 'list') {
        const componentType = normalizeListComponentType(parts[2]);
        return componentType ? { kind: 'list', path, username, componentType } : { kind: 'missing', path };
    }
    return { kind: 'component', path, username, componentType: COMPONENT_SEGMENTS[parts[1]], componentId: parts[2] };
}

function publicImage(value) {
    if (typeof value !== 'string' || !value.trim()) return SITE_IMAGE;
    try {
        const url = new URL(value.trim(), SITE_ORIGIN);
        return url.protocol === 'https:' && !url.username && !url.password ? url.href : SITE_IMAGE;
    } catch {
        return SITE_IMAGE;
    }
}

function buildPageMetadata(profile, pathname = '/', { status = 200 } = {}) {
    const route = getPageRoute(pathname);
    const name = excerpt(profile?.pageTitle, 80) || excerpt(profile?.muultiName, 80);
    const spaceDescription = excerpt(profile?.pageDescription, 120);
    const image = publicImage(profile?.profilePicUrl);
    const label = route.kind === 'list' ? COMPONENT_TYPE_LIST_OPTIONS.find(option => option.type === route.componentType)?.listLabel
        : route.kind === 'component' ? route.componentType.charAt(0).toUpperCase() + route.componentType.slice(1) + ' Panel'
        : route.kind === 'space3d' ? '3D Space' : '';
    const missing = status === 404 || route.kind === 'missing';
    const unavailable = status === 503;
    const defaultTitle = route.kind === 'post' ? 'Social Post | MuultiSpace' : SITE_TITLE;
    const url = SITE_ORIGIN + (route.kind === 'static' ? route.path : pagePath(pathname));
    return {
        title: unavailable ? 'Temporarily Unavailable | MuultiSpace' : missing ? 'Page Not Found | MuultiSpace'
            : route.definition?.[0] || (name ? (label ? label + ' — ' : '') + name + ' | MuultiSpace' : defaultTitle),
        description: unavailable ? 'This MuultiSpace page could not be loaded right now. Please try again shortly.'
            : missing ? 'This MuultiSpace page could not be found. Explore the creator directory or return to the homepage.'
            : route.definition?.[1] || SITE_DESCRIPTION + (spaceDescription ? ' ' + spaceDescription : ''),
        image,
        imageAlt: image === SITE_IMAGE ? 'MuultiSpace logo' : 'Feature image for ' + (name || 'this Space') + ' on MuultiSpace',
        url,
        canonical: missing || unavailable ? null : url,
        robots: missing || route.definition?.[2] === false
            ? 'noindex, follow' : null,
    };
}

function metadataTags(metadata) {
    const logo = metadata.image === SITE_IMAGE;
    return [
        ['name', 'description', metadata.description],
        ['name', 'robots', metadata.robots],
        ['property', 'og:site_name', 'MuultiSpace'],
        ['property', 'og:type', 'website'],
        ['property', 'og:title', metadata.title],
        ['property', 'og:description', metadata.description],
        ['property', 'og:url', metadata.url],
        ['property', 'og:image', metadata.image],
        ['property', 'og:image:alt', metadata.imageAlt],
        ['property', 'og:image:width', logo ? '1024' : null],
        ['property', 'og:image:height', logo ? '1024' : null],
        ['name', 'twitter:card', 'summary_large_image'],
        ['name', 'twitter:title', metadata.title],
        ['name', 'twitter:description', metadata.description],
        ['name', 'twitter:image', metadata.image],
        ['name', 'twitter:image:alt', metadata.imageAlt],
    ];
}

const toTimestampMs = (value) => {
    if (!value) return 0;
    if (typeof value?.toMillis === 'function') return value.toMillis();
    const asNumber = Number(value);
    if (Number.isFinite(asNumber)) return asNumber;
    return 0;
};

// Match the public page's preference for wallet-keyed profiles over legacy IDs.
const choosePreferredUserSnapshot = (docs = []) => {
    if (!Array.isArray(docs) || docs.length === 0) return null;
    const deterministicDocs = docs.filter((docSnap) => {
        const data = docSnap?.data?.() || {};
        const deterministicId = typeof data.publicKey === 'string' ? data.publicKey : '';
        return !!deterministicId && docSnap.id === deterministicId;
    });
    const candidates = deterministicDocs.length > 0 ? deterministicDocs : docs;
    let newestDoc = candidates[0];
    let newestUpdatedMs = toTimestampMs((newestDoc?.data?.() || {}).updatedAt);
    candidates.forEach((docSnap) => {
        const updatedMs = toTimestampMs((docSnap?.data?.() || {}).updatedAt);
        if (updatedMs > newestUpdatedMs) {
            newestUpdatedMs = updatedMs;
            newestDoc = docSnap;
        }
    });
    return newestDoc;
};

module.exports = {
    SITE_ORIGIN, SITE_DESCRIPTION, SITE_TITLE, SITE_IMAGE, PUBLIC_PAGES,
    COMPONENT_TYPE_LIST_OPTIONS, normalizeListComponentType, isComponentListAvailable,
    buildPageMetadata, metadataTags, getSpaceUsername, getPageRoute, choosePreferredUserSnapshot,
};
